import { Amount, FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { Box, Menu, MenuButton, Button, Flex, MenuList, MenuItem, Icon, Skeleton, Text } from '@chakra-ui/react'
import { IoIosArrowDown } from 'react-icons/io'
import { useSession } from '@/context/SessionProvider'
import { memo, ReactElement } from 'react'
import { getTokens, Token } from '@/services/token-service'
import { useBalanceStore } from '@/context/UserBalance'
import { LocalStorageKey } from '@/constants/localstorage'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { USDC_NEW_DENOM } from '@/constants/constants'

export const CurrencyDropdown = memo(({ isDisabled }: { isDisabled: boolean }) => {
  const { account } = useSession()
  const balance = useBalanceStore((state) => state.viewBalance)
  const isLoading = useBalanceStore((state) => state.isLoading)
  const denom = useBalanceStore((state) => state.denom)
  const fiat = useFiatCurrencyStore()
  const isFiatActive = !fiat.isUsd() && denom === USDC_NEW_DENOM

  if (isLoading && !!account) {
    return <Skeleton w={'140px'} height={7} startColor="ninja.500" endColor="ninja.400" rounded={'lg'} />
  }

  const setDenom = (denom: string) => {
    useBalanceStore.setState({ denom })
    localStorage.setItem(LocalStorageKey.Denom, JSON.stringify(denom))
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.capture('crypto_currency_changed', { currency: denom, type: 'crypto' })
    }
  }

  const usdcBalance = balance[denom]?.humanFormat || '0.00'

  const activeElement = isFiatActive ? (
    <Flex alignItems={'center'} gap={1.5}>
      <Text fontWeight={700} fontSize={'sm'} whiteSpace={'nowrap'}>
        {fiat.formatBalance(Number(usdcBalance))}
      </Text>
      <Text fontSize={'xs'} color={'ninja.300'} fontWeight={500} whiteSpace={'nowrap'}>
        ≈{usdcBalance} USDC
      </Text>
    </Flex>
  ) : (
    <Amount value={usdcBalance} denom={denom} iconSize={'18px'} />
  )

  return (
    <Dropdown
      setItem={(token) => setDenom(token.denom)}
      isDisabled={isDisabled}
      activeElement={activeElement}
      listElement={(token) => {
        const tokenBalance = balance[token.denom]?.humanFormat || '0.00'
        const showFiat = !fiat.isUsd() && token.denom === USDC_NEW_DENOM
        return showFiat ? (
          <Flex alignItems={'center'} gap={2} fontSize={'sm'}>
            <Text fontWeight={700} whiteSpace={'nowrap'}>
              {fiat.formatBalance(Number(tokenBalance))}
            </Text>
            <Text fontSize={'xs'} color={'ninja.300'} fontWeight={500} whiteSpace={'nowrap'}>
              ≈{tokenBalance} USDC
            </Text>
          </Flex>
        ) : (
          <Amount value={tokenBalance} wrapperStyles={{ fontSize: 'sm' }} denom={token.denom} iconSize={'18px'} />
        )
      }}
    />
  )
})

export const BankDropdown = memo(
  ({ isDisabled, denom, setDenom }: { isDisabled: boolean; denom: string; setDenom: (denom: string) => void }) => {
    const { account } = useSession()
    const balance = useBalanceStore((state) => state.balance)

    const viewBalance = Object.fromEntries(Object.entries(balance).map(([denom, value]) => [denom, value.bank]))

    const isLoading = useBalanceStore((state) => state.isLoading)

    if (isLoading && !!account) {
      return <Skeleton w={'140px'} height={7} startColor="ninja.500" endColor="ninja.400" rounded={'lg'} />
    }

    return (
      <Dropdown
        setItem={(token) => setDenom(token.denom)}
        isDisabled={isDisabled}
        activeElement={
          <FiatOrCryptoAmount value={viewBalance[denom]?.humanFormat || '0.00'} denom={denom} iconSize={'18px'} />
        }
        listElement={(token) => (
          <FiatOrCryptoAmount
            value={viewBalance[token.denom]?.humanFormat || '0.00'}
            wrapperStyles={{ fontSize: 'sm' }}
            denom={token.denom}
            iconSize={'18px'}
          />
        )}
      />
    )
  }
)

const Dropdown = ({
  setItem,
  isDisabled,
  activeElement,
  listElement,
}: {
  setItem: (token: Token) => void
  isDisabled: boolean
  activeElement: ReactElement
  listElement: (token: Token) => ReactElement
}) => {
  return (
    <Box pos={'relative'}>
      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          variant={'unstyled'}
          h={'auto'}
          isDisabled={isDisabled}
          _disabled={{
            opacity: 0.6,
          }}
        >
          <Flex
            minW={'140px'}
            p={1}
            px={2.5}
            pb={0.5}
            h={7}
            alignItems={'center'}
            justifyContent={'space-between'}
            bg={'ninja.500'}
            rounded={'lg'}
            gap={1.5}
          >
            {activeElement}
            <Icon as={IoIosArrowDown} boxSize={4} />
          </Flex>
        </MenuButton>
        <MenuList
          border={'none'}
          boxShadow={'0px 8px 48px 0px rgba(0, 0, 0, 0.20)'}
          bg={'ninja.500'}
          p={2}
          rounded={'lg'}
          minW={40}
        >
          {getTokens().map((token) => (
            <MenuItem
              key={token.denom}
              onClick={() => setItem(token)}
              h={'auto'}
              px={3}
              py={1.5}
              pb={1}
              rounded={'lg'}
              bg={'transparent'}
              w={'full'}
              _hover={{
                bg: 'ninja.600',
              }}
            >
              {listElement(token)}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  )
}
